import React from 'react';
import { ExternalLink, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../lib/theme';

function _fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

function _qboDeepLink(qbPublish, connection) {
  if (!qbPublish?.qb_transaction_id || !connection?.realm_id) return null;
  const host = (connection.environment || 'sandbox') === 'production'
    ? 'https://qbo.intuit.com'
    : 'https://sandbox.qbo.intuit.com';
  return `${host}/app/purchase?txnId=${encodeURIComponent(qbPublish.qb_transaction_id)}`;
}

/**
 * Phase 25.2 — compact "Published" badge.
 *
 * Renders a green pill with the company name + published date + external-
 * link icon that deep-links to the QBO transaction when ``connection`` is
 * passed (and we know the QBO environment).
 */
export default function PublishStatusBadge({ qbPublish, connection, compact = false }) {
  const { colors } = useTheme();
  if (!qbPublish || qbPublish.status !== 'published') return null;
  const link = _qboDeepLink(qbPublish, connection);
  const companyName = connection?.company_name || 'QuickBooks';
  const published = _fmtTime(qbPublish.published_at);

  const content = (
    <span
      data-testid="qb-published-badge"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: compact ? '2px 7px' : '4px 10px',
        borderRadius: 999,
        background: '#E7F7EE', color: '#117A3C',
        fontSize: compact ? 10 : 11, fontWeight: 700, letterSpacing: '0.01em',
        border: '1px solid #BFE8CE',
        whiteSpace: 'nowrap',
      }}
    >
      <CheckCircle2 size={compact ? 10 : 12} />
      <span>{compact ? 'QB' : `Published to ${companyName}`}</span>
      {!compact && published && (
        <span style={{ color: '#4A7E62', fontWeight: 500 }}> · {published}</span>
      )}
      {link && !compact && <ExternalLink size={11} />}
    </span>
  );

  if (!link) return content;
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ textDecoration: 'none' }}
      title={`Open QuickBooks transaction QB# ${qbPublish.qb_transaction_id}`}
    >
      {content}
    </a>
  );
}
